import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Landmark, LogOut, RefreshCw, PhoneCall, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { waLink } from "@/lib/whatsapp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rk_admin_token";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [leads, setLeads] = useState(null);
  const [loading, setLoading] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setLeads(null);
  }, []);

  const fetchLeads = useCallback(
    async (t) => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/leads`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        setLeads(data);
      } catch (e) {
        if (e.response?.status === 401) {
          logout();
          setError(formatApiErrorDetail(e.response?.data?.detail));
        } else {
          setError(formatApiErrorDetail(e.response?.data?.detail));
        }
      } finally {
        setLoading(false);
      }
    },
    [logout]
  );

  useEffect(() => {
    if (token) fetchLeads(token);
  }, [token, fetchLeads]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center px-6">
        <div className="w-full max-w-md bg-white border border-border rounded-2xl p-8 lg:p-10">
          <div className="flex items-center gap-2">
            <Landmark className="h-6 w-6 text-bronze" />
            <span className="font-serif text-2xl font-medium text-forest">
              Rajkiran <span className="text-bronze">Wealth</span>
            </span>
          </div>
          <h1 className="mt-6 font-serif text-3xl font-medium text-forest">Admin login</h1>
          <p className="mt-2 text-sm text-muted-foreground">Private area — view your call-back leads.</p>
          <form onSubmit={handleLogin} data-testid="admin-login-form" className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="admin-email">Email</Label>
              <Input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@rajkiranwealth.com"
                className="h-11"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password">Password</Label>
              <Input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11"
                required
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-600">{error}</p>
            )}
            <Button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loading}
              className="w-full h-12 bg-forest text-bone hover:bg-forest-light rounded-full"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <a href="/" data-testid="admin-back-link" className="mt-6 block text-center text-sm text-muted-foreground hover:text-forest">
            Back to website
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      <header className="bg-forest text-bone">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Landmark className="h-6 w-6 text-bronze-light" />
            <span className="font-serif text-2xl font-medium">
              Rajkiran <span className="text-bronze-light">Wealth</span>
            </span>
            <span className="ml-3 text-xs uppercase tracking-[0.2em] text-bone/60">Leads</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="admin-refresh-btn"
              variant="outline"
              size="sm"
              onClick={() => fetchLeads(token)}
              disabled={loading}
              className="border-bone/30 text-bone hover:bg-bone/10 hover:text-bone rounded-full"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              data-testid="admin-logout-btn"
              variant="outline"
              size="sm"
              onClick={logout}
              className="border-bone/30 text-bone hover:bg-bone/10 hover:text-bone rounded-full"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="font-serif text-3xl lg:text-4xl font-medium text-forest" data-testid="leads-heading">
          Call-back requests {leads ? `(${leads.length})` : ""}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Every form submission is saved here and also sent to your WhatsApp.
        </p>

        {error && <p data-testid="leads-error" className="mt-6 text-sm text-red-600">{error}</p>}

        {leads && leads.length === 0 && (
          <div data-testid="leads-empty" className="mt-10 bg-white border border-border rounded-2xl p-12 text-center">
            <Inbox className="h-10 w-10 mx-auto text-bronze" />
            <p className="mt-4 font-serif text-xl text-forest">No leads yet</p>
            <p className="mt-1 text-sm text-muted-foreground">New call-back requests will appear here.</p>
          </div>
        )}

        {leads && leads.length > 0 && (
          <div className="mt-8 space-y-4">
            {leads.map((lead) => (
              <div
                key={lead.id}
                data-testid={`lead-card-${lead.id}`}
                className="bg-white border border-border rounded-2xl p-6 flex flex-col lg:flex-row lg:items-center gap-4 justify-between"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="font-serif text-xl font-medium text-forest">{lead.name}</p>
                    <span className="text-xs font-semibold uppercase tracking-wide bg-accent text-accent-foreground rounded-full px-3 py-1">
                      {lead.service}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{lead.phone}</p>
                  {lead.note && <p className="mt-2 text-sm text-foreground/80">{lead.note}</p>}
                  <p className="mt-2 text-xs text-muted-foreground">
                    {new Date(lead.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                </div>
                <div className="flex shrink-0 gap-3">
                  <a
                    href={`tel:+91${lead.phone}`}
                    data-testid={`lead-call-${lead.id}`}
                    className="inline-flex items-center gap-2 rounded-full border border-forest px-4 py-2 text-sm font-semibold text-forest transition-colors hover:bg-forest hover:text-bone"
                  >
                    <PhoneCall className="h-4 w-4" />
                    Call
                  </a>
                  <a
                    href={waLink(`Hi ${lead.name}, this is Rajkiran Wealth. You requested a call back regarding ${lead.service}.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`lead-whatsapp-${lead.id}`}
                    className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
                  >
                    <WhatsAppIcon className="h-4 w-4" />
                    WhatsApp
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
